import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import ImageWithFallback from './ImageWithFallback';
import { Orbs, CornerAccents } from './DecorativeElements';
import { preloadImages } from '../utils/imagePreloader';
import './ServicesSection.css';

const ServicesSection = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const services = [
    {
      id: 'performance-marketing',
      title: 'Performance Marketing',
      description: 'Elevate your brand with precision-driven performance marketing solutions.',
      tagline: 'Elevate your brand with precision-driven performance marketing solutions.',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&h=800&fit=crop&q=80',
      icon: '📈',
    },
    {
      id: 'digital-marketing-analytics',
      title: 'Digital Marketing & Dashboard Analytics',
      description: 'Crafting connections, analyzing impact: your Digital success story, at your fingertips.',
      tagline: 'Crafting connections, analyzing impact: your Digital success story, at your fingertips.',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&h=800&fit=crop&q=80',
      icon: '📊',
    },
    {
      id: 'web-app-development',
      title: 'Web & App Development',
      description: 'Custom web and app solutions tailored to your business needs. We build your digital dreams into reality.',
      tagline: 'Custom web and app solutions tailored to your business needs.',
      image: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=1200&h=800&fit=crop&q=80',
      icon: '🌐',
    },
    {
      id: 'graphic-designing',
      title: 'Graphic Designing',
      description: 'Where creativity meets clarity: crafting compelling visuals that speak volumes.',
      tagline: 'Where creativity meets clarity: crafting compelling visuals that speak volumes.',
      image: 'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=1200&h=800&fit=crop&q=80',
      icon: '🎨',
    },
    {
      id: 'consulting-services',
      title: 'Consulting Services',
      description: 'Strategic consulting to transform your business and drive digital success.',
      tagline: 'Strategic consulting to transform your business and drive digital success.',
      image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=1200&h=800&fit=crop&q=80',
      icon: '💼',
    },
    {
      id: 'cyber-security',
      title: 'Cyber Security',
      description: 'Guardians of your digital realm: fortifying your defenses in an ever-evolving cyber landscape.',
      tagline: 'Guardians of your digital realm: fortifying your defenses.',
      image: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=1200&h=800&fit=crop&q=80',
      icon: '🔒',
    },
    {
      id: 'digital-strategy',
      title: 'Digital Strategy',
      description: 'Comprehensive strategies for web development, Digital management, SEO, and more.',
      tagline: 'Comprehensive strategies for web development, Digital management, SEO, and more.',
      image: 'https://images.unsplash.com/photo-1558655146-364adaf1fcc9?w=1200&h=800&fit=crop&q=80',
      icon: '📱',
    },
    {
      id: 'content-production',
      title: 'Content Production',
      description: 'From copywriting to press releases, we craft content that resonates with your audience.',
      tagline: 'From copywriting to press releases, we craft content that resonates.',
      image: 'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=1200&h=800&fit=crop&q=80',
      icon: '✍️',
    },
    {
      id: 'legal-services',
      title: 'Legal Services',
      description: 'Providing comprehensive legal services to protect your business and ensure compliance.',
      tagline: 'Providing comprehensive legal services to protect your business.',
      image: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=1200&h=800&fit=crop&q=80',
      icon: '⚖️',
    },
    {
      id: 'generative-ai',
      title: 'Generative AI',
      description: 'Leverage cutting-edge AI to automate processes, generate creative content, and unlock valuable insights.',
      tagline: 'Leverage cutting-edge AI to automate processes and unlock insights.',
      image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1200&h=800&fit=crop&q=80',
      icon: '🤖',
    },
  ];

  // Preload service images immediately when component mounts
  useEffect(() => {
    const serviceImages = services.map(service => service.image);
    preloadImages(serviceImages.slice(0, 4), {
      priority: 'high',
      stagger: false
    }).catch(() => {});
  }, []);

  const placeholderImage = (icon) => {
    const svg = encodeURIComponent(`
      <svg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300">
        <rect width="400" height="300" fill="#f8f9fa"/>
        <text x="50%" y="50%" text-anchor="middle" dominant-baseline="middle" font-family="Arial" font-size="48" fill="#ddd">${icon}</text>
        <rect width="400" height="300" fill="none" stroke="#e5e5e5" stroke-width="2"/>
      </svg>
    `);
    return `data:image/svg+xml,${svg}`;
  };

  return (
    <section id="services" className="services-section" ref={ref}>
      <Orbs />
      <CornerAccents />
      <div className="container">
        <motion.div
          className="services-header"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <div className="section-label">Our Bread & Butter</div>
          <h2 className="section-title-services">
            Crafting excellence<br />
            in every solution
          </h2>
        </motion.div>

        <div className="services-list-luxury">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              className="service-item-luxury"
              initial={{ opacity: 0, x: -50 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: index * 0.15 }}
            >
              <div className="service-number-luxury">{String(index + 1).padStart(2, '0')}</div>
              <div className="service-content-luxury">
                <div className="service-image-wrapper">
                  <ImageWithFallback
                    src={service.image}
                    alt={service.title}
                    className="service-image"
                    priority={index < 2}
                    fallback={placeholderImage(service.icon)}
                  />
                </div>
                <div className="service-header-luxury">
                  <h3>{service.title}</h3>
                  <Link to={`/services/${service.id}`} className="service-link-luxury">
                    Explore <ArrowRight size={20} />
                  </Link>
                </div>
                <p className="service-tagline">{service.tagline}</p>
                <p className="service-description">{service.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
